/**
 * useGlobalTradingContext.ts — MER Algo Footprints v9.2.0
 *
 * Phase 2: Global Trading Header (HUD) view-model.
 *
 * Aggregates the fields the persistent header needs, sourced ENTIRELY from
 * existing engines / hooks / store. NO new endpoints. NO duplicated business
 * logic. Component layer reads this VM and renders.
 *
 * ─── Source map ───────────────────────────────────────────────────────────────
 *
 *   symbol         ← useStore().activeSymbol
 *   price          ← SymbolContext.curPrice
 *   changePct      ← (price − sessionOpen) / sessionOpen × 100
 *                     sessionOpen comes from RegimeResult.levels.open with
 *                     a fallback to the first candle's open.
 *   regime         ← SymbolContext.regime.label
 *   posture        ← SymbolThesis.current.overallBias  ↘ first non-null
 *                  ← SymbolContext.regime.posture     ↙ wins
 *   confidence     ← SymbolContext.regime.confidence
 *   rvol           ← SymbolContext.regime.rvol
 *   keyLevel       ← SymbolContext.activeDecisionLevel.{price,label,distPct}
 *   keyLevelNote   ← derived: distance% from price + structural reference
 *   gamma          ← SymbolContext.gamma.{regime,gammaFlip,callWall,putWall}
 *   dataMode       ← FeedTelemetry.provider
 *   transport      ← FeedTelemetry.wsStatus → 'Stream' | 'HTTP'
 *   aiStatus       ← OrchestratorStatus.mode
 *   marketStatus   ← isMarketOpen() / isMarketDataAvailable()
 *   refreshLabel   ← derived from FeedTelemetry.lastFetchTs/lastTickTs
 *   currentTime    ← Date.now() polled at 1Hz
 *   loading, error ← symbolData[sym].{loading,error}
 *
 * ─── Empty-state contract (Phase 2 spec Step 9) ───────────────────────────────
 *
 * Every value field is null/undefined-tolerant. Components apply:
 *   value ?? (loading ? "Loading..." : "No data")
 */

import { useEffect, useState, useMemo } from 'react';
import { useStore } from '../store';
import { getSymbolContext } from '../selectors/symbolContext';
import { getThesis } from '../ai/thesisEngine';
import {
  useTelemetry,
  isMarketOpen,
  isMarketDataAvailable,
  WSConnectionStatus,
} from '../shared/hooks/useMarketData';
import { useLiveQuote } from '../shared/hooks/liveQuoteStream';
import {
  getOrchestratorStatus,
  subscribeOrchestratorStatus,
  AIMode,
} from '../ai/aiOrchestrator';

// ─── Public VM contract ──────────────────────────────────────────────────────

export interface KeyLevelVM {
  price: number;
  label: string;
  distPct: number;   // signed % distance from current price (positive = above)
}

export interface GammaVM {
  regime:   string | null;     // e.g. 'positive' | 'negative' | 'neutral'
  flip:     number | null;     // gamma flip price
  callWall: number | null;
  putWall:  number | null;
}

export type MarketStatus =
  | 'open'         // RTH 9:30–16:00 ET, weekday
  | 'extended'     // 4–9:30 AM or 4–8 PM ET, weekday
  | 'closed';

export interface GlobalTradingContextVM {
  // Instrument
  symbol:     string;
  price:      number | null;
  changePct:  number | null;

  // Decision context
  regime:     string | null;
  posture:    string | null;
  confidence: number | null;   // 0–100 from RegimeResult.confidence
  rvol:       number | null;
  keyLevel:   KeyLevelVM | null;
  keyLevelNote: string | null;
  gamma:      GammaVM;

  // System strip
  dataMode:    string;         // 'thetadata' | 'none' | …
  transport:   'Stream' | 'HTTP';
  wsStatus:    WSConnectionStatus;
  pollIntervalSec: number;
  lastFetchTs: number | null;
  lastTickTs:  number | null;
  refreshLabel: string;        // 'Live' | 'Updated 12s ago' | '—'

  // Backlog #1 (May 9): expose chart bar resolution and trader horizon for header display.
  // Pure passthrough from existing sources -- no new data dependencies.
  barInterval: string;         // chart bar resolution: e.g. '1min' | '5min' | '15min'
  horizon:     string;         // trader horizon: 'scalp' | 'intraday' | 'swing' | 'position'

  aiStatus:        AIMode;
  aiInFlight:      boolean;
  aiLastSuccessAt: number | null;

  marketStatus: MarketStatus;
  currentTime:  number;        // ms — re-renders at 1Hz

  // Status
  loading: boolean;
  error:   string | null;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function sessionOpenFromContext(
  ctx: ReturnType<typeof getSymbolContext>,
): number | null {
  if (!ctx) return null;
  const lvl = ctx.regime?.levels?.open;
  if (typeof lvl === 'number' && Number.isFinite(lvl) && lvl > 0) return lvl;
  const first = ctx.candles?.[0]?.open;
  if (typeof first === 'number' && Number.isFinite(first) && first > 0) return first;
  return null;
}

function buildKeyLevel(
  ctx: ReturnType<typeof getSymbolContext>,
): KeyLevelVM | null {
  const a = ctx?.activeDecisionLevel;
  if (!a) return null;
  return { price: a.price, label: a.label, distPct: a.distPct };
}

function buildKeyLevelNote(
  level: KeyLevelVM | null,
  ctx: ReturnType<typeof getSymbolContext>,
): string | null {
  if (!level) return null;
  const dir = level.distPct >= 0 ? 'above' : 'below';
  const abs = Math.abs(level.distPct).toFixed(2);
  const struct = ctx?.structuralLevel;
  const structPart = struct
    ? ` · structural ${struct.label} @ ${struct.price.toFixed(2)}`
    : '';
  return `${abs}% ${dir}${structPart}`;
}

function buildGamma(ctx: ReturnType<typeof getSymbolContext>): GammaVM {
  const g = ctx?.gamma ?? null;
  return {
    regime:   g?.regime ?? null,
    flip:     g?.gammaFlip ?? null,
    callWall: g?.callWall ?? null,
    putWall:  g?.putWall ?? null,
  };
}

function ageSeconds(ts: number | null, now: number): number | null {
  if (!ts) return null;
  return Math.max(0, Math.floor((now - ts) / 1000));
}

function formatRefreshLabel(
  lastFetchTs: number | null,
  lastTickTs:  number | null,
  wsStatus:    WSConnectionStatus,
  now:         number,
): string {
  if (wsStatus === 'connected' && lastTickTs) {
    const a = ageSeconds(lastTickTs, now);
    if (a == null || a < 2) return 'Live';
    return `Tick ${a}s ago`;
  }
  if (lastFetchTs) {
    const a = ageSeconds(lastFetchTs, now);
    if (a == null) return '—';
    if (a < 2) return 'Just now';
    if (a < 60) return `Updated ${a}s ago`;
    const m = Math.floor(a / 60);
    return `Updated ${m}m ${a % 60}s ago`;
  }
  return '—';
}

function deriveMarketStatus(): MarketStatus {
  if (isMarketOpen()) return 'open';
  if (isMarketDataAvailable()) return 'extended';
  return 'closed';
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useGlobalTradingContext(): GlobalTradingContextVM {
  const { activeSymbol, symbolData, settings, weights } = useStore();
  const sym = activeSymbol ?? '';

  // Telemetry — already reactive via its own listener set.
  const tel = useTelemetry();

  // Orchestrator — subscribe so the HUD updates as AI runs/finishes.
  const [aiStatus, setAiStatus] = useState(() => getOrchestratorStatus());
  useEffect(() => {
    const unsub = subscribeOrchestratorStatus(() => {
      setAiStatus(getOrchestratorStatus());
    });
    return unsub;
  }, []);

  // 1Hz clock tick — drives currentTime + age-derived labels.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // Build symbol context — same selector the legacy TopContextBar uses.
  const sd  = sym ? symbolData[sym] : undefined;
  const ctx = sym ? getSymbolContext(sym, sd, settings, weights) : null;

  // Backlog #2: subscribe to the live quote stream so the header price
  // updates at the WebSocket-poll cadence (5s) instead of the last-candle
  // cadence (60s+). Matches the pattern in WatchlistSidebar:85-87 so the
  // two views stay in sync.
  const liveQuote = useLiveQuote(sym);
  const thesis = sym ? getThesis(sym) : null;

  return useMemo<GlobalTradingContextVM>(() => {
    // Backlog #2: prefer live WebSocket-poll price (5s cadence) when fresh,
    // fall back to ctx.curPrice (last candle close, 60s+ cadence) otherwise.
    // 30s freshness threshold matches WatchlistSidebar.
    const liveAge   = liveQuote?.receivedAt ? (Date.now() - liveQuote.receivedAt) : Infinity;
    const liveFresh = liveQuote?.mid != null && liveAge < 30_000;
    const price     = liveFresh ? liveQuote!.mid : (ctx?.curPrice ?? null);

    const open  = sessionOpenFromContext(ctx);
    const changePct =
      price != null && open != null && open > 0
        ? ((price - open) / open) * 100
        : null;

    // Posture: thesis bias wins when present, fall back to deterministic regime.
    const posture =
      thesis?.current?.overallBias ?? ctx?.regime?.posture ?? null;

    const keyLevel = buildKeyLevel(ctx);
    const keyLevelNote = buildKeyLevelNote(keyLevel, ctx);

    const transport: 'Stream' | 'HTTP' =
      tel.wsStatus === 'connected' ? 'Stream' : 'HTTP';

    const dataMode = tel.provider || 'none';

    const refreshLabel = formatRefreshLabel(
      tel.lastFetchTs,
      tel.lastTickTs,
      tel.wsStatus,
      now,
    );

    return {
      symbol:     sym,
      price,
      changePct,
      regime:     ctx?.regime?.label ?? null,
      posture,
      confidence: ctx?.regime?.confidence ?? null,
      rvol:       ctx?.regime?.rvol ?? null,
      keyLevel,
      keyLevelNote,
      gamma:      buildGamma(ctx),

      dataMode,
      transport,
      wsStatus:   tel.wsStatus,
      pollIntervalSec: tel.pollIntervalSec,
      lastFetchTs: tel.lastFetchTs,
      lastTickTs:  tel.lastTickTs,
      refreshLabel,

      // Backlog #1: surfaced for header TF/HZN cell.
      barInterval: sd?.interval ?? settings.defaultInterval,
      horizon:     useStore.getState().horizon,

      aiStatus:        aiStatus.mode,
      aiInFlight:      sym ? aiStatus.inFlight.has(sym) : false,
      aiLastSuccessAt: aiStatus.lastSuccessAt,

      marketStatus: deriveMarketStatus(),
      currentTime:  now,

      loading: !!sd?.loading,
      error:   sd?.error ?? null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sym,
    sd?.loading,
    sd?.error,
    liveQuote?.mid,
    liveQuote?.receivedAt,
    ctx?.curPrice,
    ctx?.regime?.label,
    ctx?.regime?.posture,
    ctx?.regime?.confidence,
    ctx?.regime?.rvol,
    ctx?.regime?.levels?.open,
    ctx?.activeDecisionLevel?.price,
    ctx?.activeDecisionLevel?.label,
    ctx?.activeDecisionLevel?.distPct,
    ctx?.structuralLevel?.price,
    ctx?.structuralLevel?.label,
    ctx?.gamma?.regime,
    ctx?.gamma?.gammaFlip,
    ctx?.gamma?.callWall,
    ctx?.gamma?.putWall,
    thesis?.current?.overallBias,
    tel.wsStatus,
    tel.lastTickTs,
    tel.lastFetchTs,
    tel.pollIntervalSec,
    tel.provider,
    aiStatus.mode,
    aiStatus.lastSuccessAt,
    aiStatus.inFlight,
    now,
  ]);
}
